<h2 mat-dialog-title class="attach-title">
  <mat-icon>attach_file</mat-icon>
  Add an Attachment
</h2>

<mat-dialog-content class="attach-content">

  <!-- ── Step 1: pick a file ─────────────────────────────────── -->
  @if (view() === 'idle') {
    <p class="lede">Drop a document and I'll help you find where it belongs.</p>

    <app-file-drop-zone (fileSelected)="onFileSelected($event)"></app-file-drop-zone>

    @if (selectedFile()) {
      <div class="idle-actions">
        @if (isPdf && !isTooLargeForAi) {
          <button mat-flat-button color="primary" (click)="analyze()">
            <mat-icon>auto_awesome</mat-icon>
            Find where this goes
          </button>
          <button mat-stroked-button (click)="chooseManually()">I'll choose myself</button>
        } @else {
          <button mat-flat-button color="primary" (click)="chooseManually()">
            Choose where it goes
          </button>
          <p class="hint">{{ isPdf ? tooLargeForAiMessage : 'Automatic matching only works on PDFs.' }}</p>
        }
      </div>
    }
  }

  <!-- ── Working states ──────────────────────────────────────── -->
  @if (view() === 'uploading' || view() === 'analyzing' || view() === 'saving') {
    <div class="working">
      <mat-progress-spinner mode="indeterminate" diameter="40"></mat-progress-spinner>
      <p class="working-text">{{ view() === 'saving' ? 'Saving…' : statusMessage() }}</p>
      @if (view() === 'analyzing') {
        <p class="hint">Reading the document and matching it against your records.</p>
      }
    </div>
  }

  <!-- ── Error ───────────────────────────────────────────────── -->
  @if (view() === 'error') {
    <div class="error-panel">
      <mat-icon>error_outline</mat-icon>
      <p>{{ errorMessage() }}</p>
      <button mat-stroked-button (click)="startOver()">Try again</button>
    </div>
  }

  <!-- ── Step 2a: AI suggestions ─────────────────────────────── -->
  @if (view() === 'targets') {
    <div class="verdict">
      <mat-icon class="verdict-icon">auto_awesome</mat-icon>
      <span>This looks like <strong>{{ classificationLabel() }}</strong>.</span>
    </div>

    <!-- Party -->
    <section class="block">
      <h3 class="block-title">Party</h3>

      @if (swappingParty()) {
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Search parties</mat-label>
          <input matInput
                 [ngModel]="manualQuery()"
                 (ngModelChange)="onManualQueryChange($event)"
                 placeholder="Type a party name…"
                 autocomplete="off">
        </mat-form-field>

        <div class="option-list">
          @for (option of manualResults(); track option.key) {
            <button class="option-row" (click)="usePartyFromSearch(option)">
              <span class="option-label">{{ option.label }}</span>
              <span class="option-sub">{{ option.sublabel }}</span>
            </button>
          }
          @if (manualQuery().trim() && manualResults().length === 0 && !manualLoading()) {
            <p class="empty-note">No parties match “{{ manualQuery() }}”.</p>
          }
          @if (manualTruncated()) {
            <p class="empty-note">
              Showing {{ manualResults().length }} of {{ manualTotalMatches() }} — keep typing to narrow it down.
            </p>
          }
        </div>

        <button mat-button (click)="cancelPartySwap()">Cancel</button>
      } @else {
        <div class="party-card">
          <mat-icon>business</mat-icon>
          <div class="party-text">
            <div class="party-name">{{ activeMatch()?.party?.name || 'Unnamed party' }}</div>
            <div class="party-meta">
              @if (activeMatch()?.userChosen) {
                You picked this party
              } @else {
                {{ matchPercent(activeMatch()) }}% match
                @if (activeMatch()?.extractedName) {
                  <span class="party-extracted">· found as “{{ activeMatch()?.extractedName }}”</span>
                }
              }
            </div>
          </div>
        </div>
        <div class="party-actions">
          @if (partyMatches().length > 1) {
            <button mat-button (click)="useNextPartyMatch()">
              <mat-icon>swap_horiz</mat-icon>
              Try the next match
            </button>
          }
          <button mat-button (click)="startPartySwap()">Not right? Search parties</button>
        </div>
      }
    </section>

    <!-- Destinations -->
    <section class="block">
      <h3 class="block-title">Attach it to</h3>

      @if (!hasAnyTarget()) {
        <p class="empty-note">
          This party has no contracts, BAAs or COIs on file — you can still attach the document to
          the party itself, or
          <button class="link-button" (click)="toManualFromTargets()">pick something else</button>.
        </p>
      }

      @if (targets()?.contracts?.length) {
        <div class="group-label">Contracts ({{ targets()?.contracts?.length }})</div>
        @for (option of targets()!.contracts; track option.key) {
          <button class="target-row" [class.selected]="isSelected(option)" (click)="selectTarget(option)">
            <mat-icon class="radio">{{ isSelected(option) ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
            <span class="target-label">{{ option.label }}</span>
            <span class="target-sub">{{ option.sublabel }}</span>
            @if (option.expired) { <span class="chip chip-expired">expired</span> }
            @if (isSelected(option)) { <span class="chip chip-suggested">suggested</span> }
          </button>
        }
      }

      @if (targets()?.baas?.length) {
        <div class="group-label">BAAs ({{ targets()?.baas?.length }})</div>
        @for (option of targets()!.baas; track option.key) {
          <button class="target-row" [class.selected]="isSelected(option)" (click)="selectTarget(option)">
            <mat-icon class="radio">{{ isSelected(option) ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
            <span class="target-label">{{ option.label }}</span>
            <span class="target-sub">{{ option.sublabel }}</span>
            @if (option.expired) { <span class="chip chip-expired">expired</span> }
          </button>
        }
      }

      @if (targets()?.cois?.length) {
        <div class="group-label">COIs ({{ targets()?.cois?.length }})</div>
        @for (option of targets()!.cois; track option.key) {
          <button class="target-row" [class.selected]="isSelected(option)" (click)="selectTarget(option)">
            <mat-icon class="radio">{{ isSelected(option) ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
            <span class="target-label">{{ option.label }}</span>
            <span class="target-sub">{{ option.sublabel }}</span>
            @if (option.expired) { <span class="chip chip-expired">expired</span> }
          </button>
        }
      }

      <div class="group-label">Or</div>
      @if (targets()?.partyOption; as partyOption) {
        <button class="target-row" [class.selected]="isSelected(partyOption)" (click)="selectTarget(partyOption)">
          <mat-icon class="radio">{{ isSelected(partyOption) ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
          <span class="target-label">The party itself</span>
          <span class="target-sub">{{ partyOption.label }}</span>
        </button>
      }

      @if (hasAnyTarget()) {
        <button class="link-button standalone" (click)="toManualFromTargets()">
          None of these — let me pick something else
        </button>
      }
    </section>

    <ng-container *ngTemplateOutlet="metadata"></ng-container>
  }

  <!-- ── Step 2b: manual picker ──────────────────────────────── -->
  @if (view() === 'manual') {
    @if (manualReason()) {
      <div class="notice">
        <mat-icon>info_outline</mat-icon>
        <span>{{ manualReason() }}</span>
      </div>
    }

    <section class="block">
      <h3 class="block-title">What should this attach to?</h3>

      <div class="type-chips">
        @for (type of manualTypes; track type.entityType) {
          <button class="type-chip"
                  [class.selected]="manualType() === type.entityType"
                  (click)="setManualType(type.entityType)">
            <mat-icon>{{ type.icon }}</mat-icon>
            {{ type.label }}
          </button>
        }
      </div>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Search</mat-label>
        <input matInput
               [ngModel]="manualQuery()"
               (ngModelChange)="onManualQueryChange($event)"
               placeholder="Type to narrow the list…"
               autocomplete="off">
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>

      @if (manualLoading()) {
        <div class="inline-loading">
          <mat-progress-spinner mode="indeterminate" diameter="20"></mat-progress-spinner>
          <span>Loading…</span>
        </div>
      }

      <div class="option-list">
        @for (option of manualResults(); track option.key) {
          <button class="target-row"
                  [class.selected]="isSelected(option)"
                  (click)="selectTarget(option)">
            <mat-icon class="radio">{{ isSelected(option) ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
            <span class="target-label">{{ option.label }}</span>
            <span class="target-sub">{{ option.sublabel }}</span>
            @if (option.expired) { <span class="chip chip-expired">expired</span> }
          </button>
        }
        @if (!manualLoading() && manualResults().length === 0) {
          <p class="empty-note">
            @if (manualQuery().trim()) {
              Nothing matches “{{ manualQuery() }}”.
            } @else {
              Nothing to show here yet.
            }
          </p>
        }
        @if (manualTruncated()) {
          <p class="empty-note">
            Showing {{ manualResults().length }} of {{ manualTotalMatches() }}
            — {{ manualHiddenCount() }} more not listed. Type to narrow the list.
          </p>
        }
      </div>
    </section>

    <ng-container *ngTemplateOutlet="metadata"></ng-container>
  }

</mat-dialog-content>

<mat-dialog-actions align="end" class="attach-actions">
  @if (view() === 'targets' || view() === 'manual') {
    @if (errorMessage()) {
      <span class="action-error">{{ errorMessage() }}</span>
    }
    <button mat-button (click)="cancel()">Cancel</button>
    <button mat-flat-button color="primary" [disabled]="!canSave()" (click)="save()">Attach</button>
  } @else if (view() !== 'saving') {
    <!-- Cancel on the working states too, not just idle. The dialog opens with
         disableClose, so without this an upload or a two-minute AI poll would be
         a spinner the user can't escape with Escape, the backdrop, or a button.
         Excluded during 'saving' only because that write is a single short POST
         and abandoning it mid-flight would leave the outcome ambiguous. -->
    <button mat-button (click)="cancel()">Cancel</button>
  }
</mat-dialog-actions>

<!-- Shared metadata block — identical on the AI and manual paths, so the
     attachment record comes out the same whichever way the target was chosen. -->
<ng-template #metadata>
  <section class="block">
    <h3 class="block-title">Details</h3>

    <mat-form-field appearance="outline" class="full-width">
      <mat-label>Document Title</mat-label>
      <input matInput [ngModel]="documentTitle()" (ngModelChange)="documentTitle.set($event)" required>
      @if (!documentTitle().trim()) {
        <mat-hint>Required</mat-hint>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="full-width">
      <mat-label>Attachment Type</mat-label>
      <mat-select [ngModel]="attachmentType()" (ngModelChange)="attachmentType.set($event)">
        @for (type of attachmentTypes(); track type) {
          <mat-option [value]="type">{{ type }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="full-width">
      <mat-label>Description</mat-label>
      <textarea matInput rows="2"
                [ngModel]="description()"
                (ngModelChange)="description.set($event)"></textarea>
    </mat-form-field>
  </section>
</ng-template>
